import { useLazyQuery } from '@apollo/client'
import { PageProps, navigate } from 'gatsby'
import debounce from 'just-debounce'
import uniq from 'just-unique'
import { ComponentProps, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FilterSearch } from '@karakuri-web/faq-component'
import { FilterSearchLayout, SearchParams } from '@karakuri-web/faq-type'

import { BreadcrumbWidget } from './BreadcrumbWidget'
import { GQL_COMPLETE, GQL_SUGGEST } from './graphql'
import { Data } from './interface'

function canAutoSearch(useAutoSearch: boolean, location: Props['location'], q: string[]) {
  if (
    !useAutoSearch ||
    (location.pathname !== '/search' && location.pathname !== '/search/' && q.length === 0)
  ) {
    return false
  }
  return true
}

function usePlaceholder(defaultPlaceholder: string, q: string[]) {
  const [placeholder, setPlaceholder] = useState('')
  useEffect(() => {
    if (defaultPlaceholder.startsWith('[hide]')) {
      const p = defaultPlaceholder.slice(6)
      setPlaceholder(q.length > 0 ? '' : p)
    } else {
      setPlaceholder(defaultPlaceholder)
    }
  }, [defaultPlaceholder, q])
  return placeholder
}

type Props = Pick<PageProps<Data, Record<string, unknown>, SearchParams>, 'data' | 'location'> & {
  layout: FilterSearchLayout
  tags: string[]
  q: string[]
  breadcrumbWidgetProps?: ComponentProps<typeof BreadcrumbWidget>
  onSearch?: (q: string[], page?: number) => void
}
export function FilterSearchWidget({
  layout,
  data,
  location,
  // tags,
  q,
  onSearch,
  breadcrumbWidgetProps,
}: Props) {
  const botId = data.settingYaml.botId
  const tenantId = data.settingYaml.tenantId
  const waitMs = data.settingYaml.waitMs?.complete || 500
  const useAutoSearch = data.settingYaml.useAutoSearch || false
  const useSearchWithCategory = data.settingYaml.useSearchWithCategory || false
  const useKnowledgeSearch = data.settingYaml.useKnowledgeSearch || false

  const [callComplete, { data: completeResult }] = useLazyQuery<{ complete: string[] }>(
    GQL_COMPLETE
  )
  const [callSuggest, { data: suggestResult }] = useLazyQuery<{ suggest: string[] }>(GQL_SUGGEST)
  const [options, setOptions] = useState(completeResult?.complete || [])
  const [keywords, setKeywords] = useState(suggestResult?.suggest || [])
  const placeholder = usePlaceholder(data.settingYaml?.words?.search?.placeholder || '', q)
  const input = useRef('')

  const memoizedComplete = useMemo(
    () =>
      debounce(
        (keyword: string) => {
          if (!useKnowledgeSearch) {
            return
          }
          // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
          callComplete({ variables: { input: { botId, tenantId, keyword, q, tags: [] } } })
        },
        waitMs,
        false,
        true
      ),
    [botId, tenantId, callComplete, q, waitMs, useKnowledgeSearch]
  )
  const complete = useCallback((keyword: string) => memoizedComplete(keyword), [memoizedComplete])
  useEffect(() => {
    complete('')
    if (!useKnowledgeSearch) {
      return
    }
    // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
    callSuggest({ variables: { input: { botId, tenantId, q, tags: [] } } })
  }, [botId, tenantId, callSuggest, complete, q, useKnowledgeSearch])
  useEffect(() => {
    if (suggestResult?.suggest) {
      setKeywords(suggestResult?.suggest)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestResult?.suggest])
  useEffect(() => {
    if (completeResult?.complete) {
      setOptions(completeResult?.complete)
    }
  }, [completeResult])
  const handleSearch = useCallback(
    (event?: FormEvent) => {
      if (event) {
        event.preventDefault()
      }
      if (input.current) {
        const newValue = uniq([...q, ...input.current.split(/\s+/).filter(x => x)])
        handleChange(newValue)
        input.current = ''
        return
      }
      if (onSearch) {
        if (
          !useSearchWithCategory &&
          location.pathname !== '/search' &&
          location.pathname !== '/search/'
        ) {
          navigate('/search/?q=' + q.join(' '), { state: { q, page: 1 } })
          return
        }
        onSearch(q)
        return
      }
      navigate('/search/?q=' + q.join(' '), { state: { q, page: 1 } })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q]
  )
  useEffect(() => {
    if (canAutoSearch(useAutoSearch, location, q)) {
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const handleInput = (value: string) => {
    if (value) {
      complete(value)
    }
    input.current = value
  }
  const handleChange = (newValue: string[]) => {
    if (onSearch) {
      onSearch(newValue, 1)
      return
    }
    navigate('/search/?q=' + newValue.join(' '), { state: { q: newValue, page: 1 } })
  }
  const handleSelect = (value: string) => {
    handleChange(uniq([...q, value]))
  }
  return (
    // 絞り込み検索のsuggestがtenant毎に切り替わるようにkeyを設定
    <form onSubmit={handleSearch} key={tenantId}>
      <div className="faq-panel faq-filter-search">
        <h3 className="faq-panel-title">{layout.title || 'キーワード検索'}</h3>
        <FilterSearch
          title={data.settingYaml?.words?.search?.message || ''}
          onSearch={handleSearch}
          keywordProps={{
            keywords,
            title: data.settingYaml?.words?.search?.keyword || '',
            showKeywordButtonLabel: data.settingYaml?.words?.search?.showKeyword || '',
            hideKeywordButtonLabel: data.settingYaml?.words?.search?.hideKeyword || '',
            onSelect: handleSelect,
          }}
          searchProps={{
            value: q,
            placeholder,
            options,
            onInput: handleInput,
            onChange: handleChange,
          }}
        />
      </div>
      {breadcrumbWidgetProps && <BreadcrumbWidget {...breadcrumbWidgetProps} />}
    </form>
  )
}
